/* eslint-disable react/prop-types -- props documented with TypeScript interfaces */
import React, { useState, useMemo, useCallback } from 'react';
import {
  formatOrderDisplayRef,
  formatOrderRef,
} from '../../utils/orderNumberFormat';
import {
  Card,
  Table,
  Input,
  Select,
  Empty,
  Skeleton,
  Alert,
  Button,
  Tooltip,
} from 'antd';
import {
  RightOutlined,
  SearchOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { RecentOrder } from '../../services/dashboardService';
import {
  formatCurrencyVND,
  formatDateTimeVN,
  truncateMiddle,
  mapTxnStatus,
} from '../../utils/transactionHelpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface RecentOrdersTableProps {
  data: RecentOrder[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const COL_TIME = 120;
const COL_ORDER = 180;
const COL_MERCHANT = 180;
const COL_AMOUNT = 120;
const COL_STATUS = 140;
const COL_PAYMENT = 100;
const MOBILE_BREAKPOINT = 768;

const STATUS_OPTIONS = [
  { value: 'pending_payment', label: 'Chờ thanh toán' },
  { value: 'pending', label: 'Chờ xử lý' },
  { value: 'paid', label: 'Đã thanh toán' },
  { value: 'confirmed', label: 'Đã xác nhận' },
  { value: 'preparing', label: 'Đang chuẩn bị' },
  { value: 'ready', label: 'Sẵn sàng' },
  { value: 'delivering', label: 'Đang giao' },
  { value: 'delivered', label: 'Hoàn thành' },
  { value: 'completed', label: 'Hoàn thành' },
  { value: 'cancelled', label: 'Đã huỷ' },
  { value: 'payment_failed', label: 'Thất bại' },
  { value: 'failed', label: 'Thất bại' },
];

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const { label, variant } = mapTxnStatus(status);
  const showTooltip = variant === 'unknown' && status;
  return (
    <Tooltip title={showTooltip ? `Code: ${status}` : undefined}>
      <span className={`txn-status-badge txn-status-${variant}`}>
        <span className="txn-status-dot" />
        <span className="txn-status-text">{label}</span>
      </span>
    </Tooltip>
  );
}

const RecentOrdersTable: React.FC<RecentOrdersTableProps> = ({
  data,
  loading = false,
  error = null,
  onRetry,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [paymentFilter, setPaymentFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'created_at' | 'total'>(
    'created_at'
  );
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');

  const debouncedSetSearch = useMemo(
    () => debounce((v: string) => setSearch(v), 200),
    []
  );

  const paymentOptions = useMemo(() => {
    const methods = new Set<string>();
    data.forEach(r => {
      const pm = (r.payment_method || '').trim().toUpperCase();
      if (pm) methods.add(pm);
    });
    return Array.from(methods)
      .sort()
      .map(m => ({ value: m, label: m }));
  }, [data]);

  const filteredAndSorted = useMemo(() => {
    let list = [...data];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const code = (
          r.order_number || formatOrderRef(r.id, r.created_at)
        ).toLowerCase();
        const merchant = (
          r.merchant_name ||
          r.restaurant_name ||
          ''
        ).toLowerCase();
        return code.includes(q) || merchant.includes(q);
      });
    }
    if (statusFilter.length > 0) {
      const lowered = statusFilter.map((s: string) => s.toLowerCase());
      list = list.filter(r => lowered.includes((r.status || '').toLowerCase()));
    }
    if (paymentFilter) {
      list = list.filter(
        r => (r.payment_method || '').toUpperCase() === paymentFilter
      );
    }
    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'created_at') {
        cmp =
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else {
        const at = a.total ?? a.total_amount ?? 0;
        const bt = b.total ?? b.total_amount ?? 0;
        cmp = at - bt;
      }
      return sortOrder === 'ascend' ? cmp : -cmp;
    });
    return list;
  }, [data, search, statusFilter, paymentFilter, sortField, sortOrder]);

  const handleSort = useCallback((field: 'created_at' | 'total') => {
    setSortField(field);
    setSortOrder((prev: 'ascend' | 'descend') =>
      prev === 'ascend' ? 'descend' : 'ascend'
    );
  }, []);

  const renderOrderLink = (record: RecentOrder) => {
    const raw = record.order_number
      ? record.order_number.startsWith('#')
        ? record.order_number
        : `#${record.order_number}`
      : formatOrderDisplayRef(record.id, record.created_at);
    const display = raw.length > 14 ? truncateMiddle(raw, 6, 4) : raw;
    return (
      <Tooltip title={raw}>
        <button
          type="button"
          className="txn-order-link"
          onClick={e => {
            e.stopPropagation();
            navigate(`/orders/${record.id}`);
          }}
        >
          {display}
        </button>
      </Tooltip>
    );
  };

  const renderMerchant = (record: RecentOrder) => {
    const name = record.merchant_name || record.restaurant_name || '—';
    return (
      <Tooltip title={name}>
        <span className="txn-merchant-text">{name}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: (
        <button
          type="button"
          className="txn-sort-header"
          onClick={() => handleSort('created_at')}
        >
          Thời gian{' '}
          {sortField === 'created_at' && (sortOrder === 'ascend' ? '↑' : '↓')}
        </button>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: COL_TIME,
      fixed: 'left' as const,
      render: (v: string) => formatDateTimeVN(v),
    },
    {
      title: 'Mã đơn',
      key: 'order_number',
      width: COL_ORDER,
      render: (_: unknown, r: RecentOrder) => renderOrderLink(r),
    },
    {
      title: 'Merchant',
      key: 'merchant',
      width: COL_MERCHANT,
      ellipsis: { showTitle: false },
      render: (_: unknown, r: RecentOrder) => renderMerchant(r),
    },
    {
      title: (
        <button
          type="button"
          className="txn-sort-header"
          onClick={() => handleSort('total')}
        >
          Số tiền{' '}
          {sortField === 'total' && (sortOrder === 'ascend' ? '↑' : '↓')}
        </button>
      ),
      key: 'amount',
      width: COL_AMOUNT,
      align: 'right' as const,
      render: (_: unknown, r: RecentOrder) => {
        const amount = r.total ?? r.total_amount ?? 0;
        return amount > 0 ? formatCurrencyVND(amount) : '—';
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: COL_STATUS,
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: 'Thanh toán',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: COL_PAYMENT,
      render: (pm: string) => (pm ? String(pm).toUpperCase() : '—'),
    },
  ];

  const minTableWidth =
    COL_TIME + COL_ORDER + COL_MERCHANT + COL_AMOUNT + COL_STATUS + COL_PAYMENT;

  if (error) {
    return (
      <Card className="admin-recent-card txn-card">
        <div className="txn-card-header">
          <div>
            <div className="txn-title">Latest Transactions</div>
            <div className="txn-subtitle">
              Giao dịch mới nhất trong khoảng thời gian đã chọn (tối đa 12 đơn).
            </div>
          </div>
        </div>
        <Alert
          message="Không tải được dữ liệu"
          description={error}
          type="error"
          showIcon
          action={
            onRetry ? (
              <Button size="small" icon={<RetweetOutlined />} onClick={onRetry}>
                Thử lại
              </Button>
            ) : undefined
          }
          className="txn-error-alert"
        />
      </Card>
    );
  }

  return (
    <Card className="admin-recent-card txn-card">
      <div className="txn-card-header">
        <div className="txn-header-left">
          <div>
            <div className="txn-title">Latest Transactions</div>
            <div className="txn-subtitle">
              Giao dịch mới nhất trong khoảng thời gian đã chọn (tối đa 12 đơn).
            </div>
          </div>
        </div>
        <div className="txn-header-actions">
          <Input
            placeholder="Tìm mã đơn / merchant…"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            allowClear
            className="txn-search-input"
            onChange={e => debouncedSetSearch(e.target.value)}
            onPressEnter={e => setSearch((e.target as HTMLInputElement).value)}
          />
          <Select
            placeholder="Trạng thái"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            className="txn-filter-select"
            options={STATUS_OPTIONS}
            value={statusFilter.length ? statusFilter : undefined}
            onChange={v => setStatusFilter(v || [])}
          />
          <Select
            placeholder="Thanh toán"
            allowClear
            className="txn-filter-select"
            options={paymentOptions}
            value={paymentFilter || undefined}
            onChange={v => setPaymentFilter(v || null)}
          />
          <a
            className="txn-view-all"
            href="/orders"
            onClick={e => {
              e.preventDefault();
              navigate('/orders');
            }}
            aria-label="Xem tất cả đơn hàng"
          >
            Xem tất cả <RightOutlined style={{ fontSize: 12, marginLeft: 2 }} />
          </a>
        </div>
      </div>

      {loading ? (
        <div className="txn-skeleton-wrap">
          <div className="txn-skeleton-header" />
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton.Input
              key={i}
              active
              style={{ width: '100%', height: 48, marginBottom: 8 }}
            />
          ))}
        </div>
      ) : isMobile ? (
        <div className="txn-mobile-list">
          {filteredAndSorted.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  Chưa có giao dịch trong khoảng thời gian đã chọn.
                  <br />
                  <span className="txn-empty-hint">
                    Thử mở rộng khoảng ngày để xem thêm.
                  </span>
                </span>
              }
              className="txn-empty"
            />
          ) : (
            filteredAndSorted.map(record => {
              const amount = record.total ?? record.total_amount ?? 0;
              const merchant =
                record.merchant_name || record.restaurant_name || '—';
              const orderCode = record.order_number
                ? record.order_number.startsWith('#')
                  ? record.order_number
                  : `#${record.order_number}`
                : formatOrderDisplayRef(record.id, record.created_at);
              return (
                <div
                  key={record.id}
                  className="txn-mobile-card"
                  onClick={() => navigate(`/orders/${record.id}`)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/orders/${record.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="txn-mobile-row-main">
                    <span className="txn-mobile-order">{orderCode}</span>
                    <StatusBadge status={record.status} />
                  </div>
                  <div className="txn-mobile-merchant">{merchant}</div>
                  <div className="txn-mobile-row-meta">
                    <span className="txn-mobile-amount">
                      {amount > 0 ? formatCurrencyVND(amount) : '—'}
                    </span>
                    <span className="txn-mobile-meta">
                      {formatDateTimeVN(record.created_at)}
                      {record.payment_method
                        ? ` • ${String(record.payment_method).toUpperCase()}`
                        : ''}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="txn-table-wrap">
          <Table
            className="txn-table"
            columns={columns}
            dataSource={filteredAndSorted}
            rowKey="id"
            pagination={false}
            size="middle"
            scroll={{ x: minTableWidth }}
            onRow={record => ({
              onClick: () => navigate(`/orders/${record.id}`),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      Chưa có giao dịch trong khoảng thời gian đã chọn.
                      <br />
                      <span className="txn-empty-hint">
                        Thử mở rộng khoảng ngày để xem thêm.
                      </span>
                    </span>
                  }
                  className="txn-empty"
                />
              ),
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default RecentOrdersTable;
